import React, {Component, Fragment} from 'react';
import {state} from "./state/State";
import {Provider} from "mobx-react";
import {produce} from "immer";
import Midi from "./components/Midi";
import MidiPortsSelect from "./components/MidiPortsSelect";
import parseMidi from "parse-midi";
import {NOTE_NAME} from "./utils/midi";
import {
    BEND_DEFAULT,
    DISPLAY_DATA, DISPLAY_GRAPH,
    loadPreferences,
    savePreferences,
    TIMBRE_DEFAULT
} from "./utils/preferences";
import './App.css';
import {MidiSupportWarning} from "./components/MidiSupportWarning";

// const MAX_MESSAGES_PER_SECOND = 50;

const PRESSURE_TYPE_CC11 = 1;
const PRESSURE_TYPE_CHANNEL = 2;
const PRESSURE_TYPE_KEY = 3;

const PRESSURE_TYPE_LABEL = [
    '',
    'CC11: ',
    'Ch AT: ',
    'Poly AT: '
]

class App extends Component {

    state = {
        display: DISPLAY_GRAPH,
        bend_range: 48,
        bend_select: "48",
        bend_custom: "",
        timbre_cc: 74,                // number
        // z_cc_type: CHAN_PRESS,   // string
        master_channel: "1",
        ch: Array.from({length: 16}, u => ({        // left display
            timestamp: 0,
            note_on: -1,
            note_off: -1,
            velocity: 0,        // initial velocity
            notes_counter: 0,
            aftertouch: "",     // string: <Ch.|Poly>: <value>
            bend: -1,
            cc_num: -1,
            cc_val : 0
        })),
        notes: [],                  // a note is {ch:number, number:number, velocity:number, bend:number, timbre: number, pressure:number}
        bend_test: 8192,
        pressure_test: 0
    };

    // message receiver rate limiting:
    timestamp_last_message = 0;     // we'll use Date.now()

    setDisplayMode = (e) => {
        this.setState({display: parseInt(e.target.value)});
    };

    clearMessages = (e) => {
        this.setState({
            ch: Array.from({length: 16}, u => ({
                timestamp: 0,
                note_on: -1,
                note_off: -1,
                notes_counter: 0,
                aftertouch: "",
                bend: -1,
                cc_num: -1,
                cc_val: 0
            }))});
    };

    setBendRange = (e) => {
        const v = typeof e === "string" ? e : e.target.value;
        const n = v === "custom" ? parseInt(this.state.bend_custom, 10) : parseInt(v, 10);
        this.setState(produce(
            draft => {
                draft.bend_select = v;
                if (!isNaN(n)) {
                    // console.log("setBendRange invalid range", v, this.state.bend_custom);
                // } else {
                    draft.bend_range = n;
                }
            }
        ));
        savePreferences({bend_select: v});
        if (v === "custom") {
            if (!isNaN(n)) {
                savePreferences({bend_custom: n});
            }
        }
    };

    setBendCustom = (e) => {
        const v = e.target.value;
        const n = parseInt(v, 10);
        this.setState(produce(
            draft => {
                draft.bend_custom = v;
                if (isNaN(n)) {
                    // console.log("setBendCustom invalid range", v);
                } else {
                    draft.bend_range = n;
                }
            }
        ));
        savePreferences({bend_custom: v});
    };

    setYCC = (e) => {
        const v = parseInt(e.target.value, 10);
        this.setState({timbre_cc: v});
        savePreferences({timbre_cc: v});
    };

/*
    setZCC = (e) => {
        const v = e.target.value;
        this.setState({z_cc_type: v});
        savePreferences({z_cc_type: v});
    };
*/

    setMasterChannel = (e) => {
        this.setState({master_channel: e.target.value});
    };

    getBendInSemitones = (pitchBend) => {

        // min:        0
        // center:  8192
        // max:    16383

        // To map [min, max] to [-1, 1], while ensuring center 8192 is exactly 0,
        // we need to divide by different values depending on whether the pitch
        // bend is up or down, as up has 1 less possible value.

        const divider = pitchBend <= 8192 ? 8192 : (8192 - 1);
        const factor = (pitchBend - 8192) / divider;
        return factor * this.state.bend_range;
    };



    handleMidiInputEvent = (e) => {

        // We limit pitchbend messages rate
        // Q: how to not discard the last message?
        // A: --> buffer

        // let now = Date.now();
        // if (now - this.timestamp_last_message < (1000 / MAX_MESSAGES_PER_SECOND)) {
        //     return;
        // }
        // this.timestamp_last_message = now;

        if (e.data[0] === 0xF8) {
            // we ignore Timing Clock messages
            return;
        }
        const p = parseMidi(e.data);

        // console.log(p);

        this.setState(produce(
            draft => {
                const channel = p.channel - 1;
                switch (p.messageType) {

                    case "noteoff":

                        draft.ch[channel].note_off = p.key;
                        if (draft.ch[channel].note_on === p.key) {
                            draft.ch[channel].note_on = -1;
                        }

                        draft.notes.forEach((note, i) => {
                            if (note.ch === channel && note.number === p.key) {
                                delete (draft.notes[i]);
                            }
                        });

                        if (draft.ch[channel].notes_counter > 0) {
                            draft.ch[channel].notes_counter--;
                        }

                        if (draft.ch[channel].notes_counter === 0) {
                            draft.ch[channel].cc_num = -1;
                            draft.ch[channel].bend = -1;
                            draft.ch[channel].aftertouch = "";
                        }
                        break;

                    case "noteon":

                        draft.ch[channel].note_on = p.key;
                        draft.ch[channel].note_off = -1;
                        draft.ch[channel].velocity = p.velocity;
                        draft.ch[channel].notes_counter++;
                        //
                        // Add note:
                        //
                        //TODO: check if same note in same channel already exist (should not happen)
                        //
                        draft.notes.push({
                            ch: channel,
                            number: p.key,
                            velocity: p.velocity,
                            pressure: p.velocity,   // arbitrary choice
                            bend: BEND_DEFAULT,
                            timbre: TIMBRE_DEFAULT,
                            pressureType: 0
                        });

                        break;

                    case "controlchange":

                        draft.ch[channel].cc_num = p.controlNumber;
                        draft.ch[channel].cc_val = p.controlValue === 0 ? '0' : (p.controlValue || '');

                        const value = p.controlValue;
                        //
                        // Timbre
                        //
                        if (p.controlNumber === draft.timbre_cc) {   // here note is a string because it is the key of the object
                            // 3rd dim applies to ALL notes in the channel
                            draft.notes.forEach((note, i) => {
                                if (note.ch === channel) {
                                    // console.log("update timbre", value);
                                    draft.notes[i].timbre = value;
                                }
                            });
                        }

                        //
                        // Pressure CC11
                        //
                        // if (this.state.z_cc_type === CC11 && p.controlNumber === 11) {
                        if (p.controlNumber === 11) {
                            // pressure dim applies to ALL notes in the channel
                            draft.notes.forEach((note, i) => {
                                if (note.ch === channel) {
                                    draft.notes[i].pressure = p.controlValue;
                                    draft.notes[i].pressureType = PRESSURE_TYPE_CC11;
                                }
                            });
                        }
                        break;

                    case "channelpressure":
                        //
                        // Channel Pressure
                        //
                        draft.ch[channel].aftertouch = `Ch AT: ${p.pressure}`;

                        // if (this.state.z_cc_type === CHAN_PRESS) {
                            // pressure applies to ALL notes in the channel
                            draft.notes.forEach((note, i) => {
                                if (note.ch === channel) {
                                    draft.notes[i].pressure = p.pressure;
                                    draft.notes[i].pressureType = PRESSURE_TYPE_CHANNEL;
                                }
                            });
                        // }
                        break;

                    case "keypressure":
                        //
                        // Poly Pressure
                        //
                        draft.ch[channel].aftertouch = `Poly AT: ${p.pressure}`;

                        // console.log(this.state.z_cc_type, POLY_PRESS, channel, p.key);

                        // if (this.state.z_cc_type === POLY_PRESS) {
                            //TODO: check that poly pressure only applies to target key
                            draft.notes.forEach((note, i) => {
                                if (note.ch === channel && note.number === p.key) {
                                    draft.notes[i].pressure = p.pressure;
                                    draft.notes[i].pressureType = PRESSURE_TYPE_KEY;
                                }
                            });
                        // }
                        break;

                    case "pitchbendchange":

                        draft.ch[channel].bend = p.pitchBend;
                        // pitch bend applies to ALL notes in the channel
                        draft.notes.forEach((note, i) => {
                            if (note.ch === channel) {
                                draft.notes[i].bend = p.pitchBend;
                            }
                        });
                        break;

                    default:
                        // console.log("unknown message", p);
                }
            }
        ));

    };

    componentDidMount() {
        const s = loadPreferences();
        //if (s.bend_range) this.setBendRange(s.bend_range.toString(10));
        this.setState({
            bend_select: s.bend_select,
            bend_custom: s.bend_custom,
            timbre_cc: s.timbre_cc,
            // z_cc_type: s.z_cc_type
        });
        this.setBendRange(s.bend_select);
    }

    render() {

        const ycc = [];
        for (let i=0; i<128; i++) {
            ycc.push(<option key={i} value={i}>CC {i}</option>);
        }

        return (
            <Provider state={state}>
                <div className="app">

                    <MidiSupportWarning />

                    <Midi messageType={"midimessage"} onMidiInputEvent={this.handleMidiInputEvent}/>

                    <div className="header">
                        <MidiPortsSelect messageType={"midimessage"} onMidiInputEvent={this.handleMidiInputEvent} />
                        <div>
                            <label>Pitch Bend:</label>
                            <select value={this.state.bend_select} onChange={this.setBendRange}>
                                <option value="2">+/- 2</option>
                                <option value="3">+/- 3</option>
                                <option value="12">+/- 12</option>
                                <option value="24">+/- 24</option>
                                <option value="48">+/- 48</option>
                                <option value="custom">custom</option>
                            </select>
                            {this.state.bend_select === "custom" &&
                            <input type="text" value={this.state.bend_custom} onChange={this.setBendCustom} size="3"/>}
                            <span className="pl">semitones</span>
                        </div>
{/*
                        <div>
                            <label>Pressure:</label>
                            <select value={this.state.z_cc_type} onChange={this.setZCC}>
                                <option value={CHAN_PRESS}>Channel Aftertouch</option>
                                <option value={POLY_PRESS}>Poly Aftertouch</option>
                                <option value={CC11}>CC #11</option>
                            </select>
                        </div>
*/}
                        <div>
                            <label>Timbre:</label>
                            <select value={this.state.timbre_cc} onChange={this.setYCC}>
                                {ycc}
                            </select>
                        </div>
                        <div className="about">
                            <div className="bold">MPE Monitor {process.env.REACT_APP_VERSION}</div>
                            <div>by&nbsp;<a href="https://studiocode.dev" target="_blank" rel="noopener noreferrer">StudioCode.dev</a></div>
                        </div>
                    </div>

                    <div className="content row">

                        <div>
                            <div className="h15rem">
                                <button className="float-right" type="button" onClick={this.clearMessages}>clear</button>
                                <div className="bold">MIDI info</div>
                            </div>
                            <div className="channels-grid bg-white">
                                <div className="h c1">Ch</div>
                                <div className="h note-header">
                                    <div className="h">Note</div>
                                    <div className="middle-c-info">(Middle C is C4)</div>
                                </div>
                                {/*<div className="h">Velocity</div>*/}
                                <div className="h">Bend</div>
                                <div className="h">Pressure</div>
                                <div className="h">Timbre</div>
                                {this.state.ch.map(
                                    (ch, i) =>
                                        <Fragment key={`${ch}.${i}`}>
                                            <div className="c1">{i+1}</div>
                                            <div>
                                                {/*<div>{ch.note_on >= 0 ? ch.note_on : ''}</div>*/}
                                                {/*{ch.note_on >= 0 && <div className="ml20">({NOTE_NAME[ch.note_on]})</div>}*/}
                                                {this.state.notes.map(
                                                    (p, q) => {
                                                        if (p.ch === i) {
                                                            return <div className="note-name-vel" key={p.number}>
                                                                        <div className="note-number">{p.number}</div>
                                                                        <div className="note-name">{NOTE_NAME[p.number]}</div>
                                                                        <div className="note-velocity">velo: {p.velocity}</div>
                                                                    </div>
                                                        } else {
                                                            return null;
                                                        }
                                                    }
                                                )}
                                            </div>
                                            {/*<div>{ch.note_on >= 0 && ch.velocity > 0 ? ch.velocity : ''}</div>*/}
                                            <div className="align-right">
                                                {ch.notes_counter && ch.bend >= 0 ? ch.bend : ''}
                                            </div>
                                            {/*<div>{ch.aftertouch}</div>*/}
                                            <div>
                                                {this.state.notes.map(
                                                    (p, q) => {
                                                        if (p.ch === i) {
                                                            return <div key={p.number}>
                                                                {PRESSURE_TYPE_LABEL[p.pressureType]} {p.pressure}
                                                            </div>
                                                        } else {
                                                            return null;
                                                        }
                                                    }
                                                )}
                                            </div>
                                            <div>
                                                {this.state.notes.map(
                                                    (p, q) => {
                                                        if (p.ch === i) {
                                                            return <div key={p.number} className="align-right">
                                                                {p.timbre}
                                                            </div>
                                                        } else {
                                                            return null;
                                                        }
                                                    }
                                                )}
{/*
                                                <div>{ch.cc_num >= 0 ? `#${ch.cc_num}:` : ''}</div>
                                                <div className="ml10">{ch.cc_num >= 0 ? ch.cc_val : ''}</div>
*/}
                                            </div>
                                        </Fragment>
                                )}
                            </div>
                        </div>

                        <div className={this.state.display === DISPLAY_GRAPH ? 'fg' : 'fg'}>
                            <div className="row h15rem">
                                <div className="bold space-right-xl">Notes</div>
                                <label className="normal space-right-l"><input type="radio" name="voice_display_mode" value={DISPLAY_GRAPH} checked={this.state.display === DISPLAY_GRAPH} onChange={this.setDisplayMode} /> graph</label>
                                <label className="normal"><input type="radio" name="voice_display_mode" value={DISPLAY_DATA} checked={this.state.display === DISPLAY_DATA} onChange={this.setDisplayMode} /> data</label>
                            </div>

                            {this.state.display === DISPLAY_DATA &&
                            <div>
                                <div className="data-grid bg-white">
                                    <div className="h c1 span2">Initial Note</div>
                                    <div className="h span3">Pitch Bend</div>
                                    <div className="h"></div>
                                    <div className="h"></div>

                                    <div className="h c1">note</div>
                                    <div className="h">velocity</div>
                                    <div className="h">absolute</div>
                                    <div className="h">relative</div>
                                    <div className="h">semitones</div>
                                    {/*<div className="h">note</div>*/}
                                    <div className="h">Pressure</div>
                                    <div className="h">Timbre</div>
                                    {this.state.notes.map(
                                        (note, i) => {
                                            const semi = this.getBendInSemitones(note.bend);
                                            // const note_bended = Math.round(note.number + semi);
                                            // const semi_rel = Math.abs(Math.round(semi * 100)) % 100;
                                            return (
                                                <Fragment key={`${note.ch}.${note.number}`}>
                                                    <div className="f c1">
                                                        <div>{note.number}</div>
                                                        <div className="ml20">({NOTE_NAME[note.number]})</div>
                                                    </div>
                                                    <div>{note.velocity}</div>
                                                    <div>{note.bend}</div>
                                                    <div>{note.bend - 8192}</div>
                                                    <div>{semi.toFixed(2)}</div>
{/*
                                                    <div className="cell-grid-note">
                                                        <div className="cgn2">{semi < 0 && semi_rel > 5 && semi_rel < 95 ? '↙' : ''}</div>
                                                        <div className="cgn3">{note_bended}</div>
                                                        <div className="cgn4">{NOTE_NAME[note_bended]}</div>
                                                        <div className="cgn4">{semi > 0 && semi_rel > 5 && semi_rel < 95 ? '↗' : ''}</div>
                                                    </div>
*/}
                                                    <div>{note.pressure}</div>
                                                    <div>{note.timbre}</div>
                                                </Fragment>
                                            );
                                        }
                                    )}
                                </div>
                            </div>}

                            {this.state.display === DISPLAY_GRAPH &&
                            <div>
                                <div className="voice-rows">
                                {this.state.notes.map(
                                    (note, i) => {
                                        const bend = note.bend;
                                        const semi = this.getBendInSemitones(bend);
                                        const note_bended = Math.round(note.number + semi);
                                        const DISC_R = 20;
                                        const BOX_W = 600;
                                        const BEND_W = (BOX_W - 2*DISC_R) / 2;
                                        const BOX_H = 4*DISC_R;
                                        // const BEND_H = (BOX_H - 2*DISC_R) / 2;
                                        const BEND_H = DISC_R;
                                        // const fill = `hsla(0, 0%, ${80 * (127 - z) / 127}%)`;
                                        const fill = `hsl(240, ${60 * (1 - (note.pressure / 127))}%, ${60 * (1 - (note.pressure / 127))}%)`;
                                        return (
                                            <div key={i} className="voice-row">
                                                <div className="voice-graph bg-white">
                                                    <div className="voice-xyz">
                                                        <div>bend: <span className="bold">{semi.toFixed(2)}</span> semitones</div>
                                                        <div>pressure: <span className="bold">{note.pressure}</span></div>
                                                        <div>timbre: <span className="bold">{note.timbre}</span></div>
                                                    </div>
                                                    <svg viewBox={`0 0 ${BOX_W} ${BOX_H}`} xmlns="http://www.w3.org/2000/svg">
                                                        <line x1={DISC_R} y1={BOX_H/2} x2={BOX_W - DISC_R} y2={BOX_H/2} strokeWidth={0.8} />
                                                        <line x1={BOX_W/2} y1={DISC_R} x2={BOX_W/2} y2={DISC_R + 2*DISC_R} strokeWidth={0.8} />
                                                        <circle
                                                            cx={DISC_R + BEND_W + BEND_W * ((bend - 8192) / 8192)}
                                                            cy={DISC_R + 2 * BEND_H * ((127 - note.timbre) / 127)}
                                                            r={((DISC_R - 5) - 2) * note.pressure / 127 + 5}
                                                            stroke={fill} fill={fill} />
                                                        <text x={DISC_R + BEND_W + BEND_W * ((bend - 8192) / 8192)}
                                                              y={DISC_R + 2 * BEND_H * ((127 - note.timbre) / 127) + 2.55}>{NOTE_NAME[note_bended]}</text>
                                                    </svg>
                                                </div>
                                            </div>
                                        );
                                    }
                                )}
                                </div>
                            </div>}

                        </div>
                    </div>
{/*
                    <div>
                        <PianoKeyboard />
                    </div>
*/}
                    <div className="info-table">
                        <div className="h">Bend :</div>
                        <div><span className="bold">Pitch Bend</span>. (Channel Voice Message)<br />
                        The message includes the channel and a 14 bits values. It does not include any note information.<br />
                        This message affects all the notes of the channel.<br />
                        The message's value is coded with 14 bits, which gives a range of 0..16383 or -8192..+8191.</div>
                        <div className="h">Ch AT :</div>
                        <div><span className="bold">Channel Pressure (Aftertouch)</span>. (Channel Voice Message)<br />
                        The message includes the channel and the pressure. It does not include any note information.<br />
                        This message affects all the notes of the channel.<br />
                        The message's value is coded with 8 bits, which gives a range of 0..127.</div>
                        <div className="h">Poly AT :</div>
                        <div><span className="bold">Polyphonic Key Pressure (Aftertouch)</span>. (Channel Voice Message)<br />
                        The message includes the channel, the pressure and the note affected.<br />
                        This message only affects the note specified in the message.<br />
                        The message's value is coded with 8 bits, which gives a range of 0..127.</div>
                        <div className="h">CC11 :</div>
                        <div><span className="bold">Expression Controller</span>. (Channel Voice Message)<br />
                        The message includes the channel and a value. It does not include any note information.<br />
                        This message affects all the notes of the channel.<br />
                        This usually denotes a percentage of the volume. In MPE, it can be used to indicates the pressure.<br />
                        The message's value is coded with 8 bits, which gives a range of 0..127.<br />
                        Expression Controller can also be sent with a 14 bits value, using CC11 for the LSB (least signigicant byte) and CC43 for the MSB (most significant byte).<br />
                        This is possible with the LinnStrument for example. But please note that this application does not yet support this kind of message. </div>
                        <div className="h">Timbre :</div>
                        <div><span className="bold">Y movement</span>.<br />
                        CC74 (Generic Sound Controller Channel Voice Message #74) is commonly used to represent the Y dimension of MPE.
                        </div>
                    </div>
                    <div className="more-infos">
                        <p>
                            Check out our <a href="https://studiocode.dev/mpe-tester/" target="_blank" rel="noopener noreferrer">MPE Tester</a> to generate MPE data.
                        </p>
                        <p>
                            <a href="https://studiocode.dev/resources/mpe/" target="_blank" rel="noopener noreferrer">More infos about MPE</a>.
                        </p>
                    </div>
                </div>
            </Provider>
        );
    }

}

export default App;
